export default {
  common: {
    appName: 'UniPortal',
    byUnifiber: 'by Unifiber',
    copyright: '© {{year}} Unifiber — UniPortal',
    nav: { documents: 'Documents', publishing: 'Publishing', collections: 'Collections', channels: 'Drop folders', users: 'Users', publishers: 'Categories', guide: 'Guide' },
    signOut: 'Sign out',
    loading: 'Loading…',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
    search: 'Search',
    retry: 'Try again',
    error: 'Something went wrong. Please try again.',
    uncategorized: 'Uncategorized',
  },
  auth: {
    login: {
      title: 'Sign in',
      subtitle: 'Access documents shared with you',
      emailLabel: 'Email address',
      emailPlaceholder: 'you@company.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Your password',
      forgotPassword: 'Forgot password?',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      or: 'OR',
      microsoftSso: 'Sign in with Microsoft',
      ssoUnavailable: 'Microsoft sign-in is not available',
      errorBothFields: 'Please enter your email and password.',
      errorGeneric: 'Sign-in failed. Please try again.',
      ssoErrors: {
        denied: 'Microsoft sign-in was declined. Contact your administrator if you believe this is a mistake.',
        server_error: 'Microsoft sign-in is temporarily unavailable. Please try again later.',
        unknown: 'Microsoft sign-in failed. Please try again.',
      },
      noAccount: 'Accounts are created by your administrator.',
    },
    twofactor: {
      title: 'Two-factor authentication',
      subtitle: 'Enter the 6-digit code from your authenticator app',
      verify: 'Verify',
      verifying: 'Verifying…',
      rememberDevice: 'Remember this device for 30 days',
      useRecovery: 'Use a recovery code instead',
      recoveryTitle: 'Recovery code',
      recoverySubtitle: 'Enter one of your single-use recovery codes',
      recoveryPlaceholder: 'e.g. A1B2C3D4',
      backToCode: 'Back to authenticator code',
      remainingCodes: '{{count}} recovery codes remaining',
      invalidCode: 'Invalid code. Please try again.',
      sessionExpired: 'Your sign-in session has expired. Please go back and sign in again.',
      backToLogin: 'Back to sign-in',
    },
    totpSetup: {
      title: 'Set up two-factor authentication',
      subtitle: 'Protect your account with an authenticator app',
      step1: 'Scan this QR code with your authenticator app (Microsoft Authenticator, Google Authenticator, …)',
      manualEntry: 'Or enter this key manually:',
      step2: 'Then enter the 6-digit code from the app to confirm',
      confirm: 'Confirm',
      confirming: 'Confirming…',
      recoveryTitle: 'Save your recovery codes',
      recoverySubtitle:
        'Each code can be used once to sign in if you lose access to your authenticator. Store them somewhere safe — they will not be shown again.',
      download: 'Download .txt',
      copy: 'Copy',
      copied: 'Copied',
      savedConfirm: 'I have saved my recovery codes',
      continue: 'Continue to UniPortal',
    },
    forceChange: {
      title: 'Set a new password',
      subtitle: 'Your password has expired and must be changed',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      mismatch: 'Passwords do not match.',
      submit: 'Change password',
      submitting: 'Changing…',
      policy: {
        minLength: 'At least 12 characters',
        upper: 'One uppercase letter',
        lower: 'One lowercase letter',
        number: 'One number',
      },
    },
    forgot: {
      title: 'Reset your password',
      subtitle: "Enter your email address and we'll send you a reset link",
      submit: 'Send reset link',
      submitting: 'Sending…',
      sentNote: 'If an account with that email exists, a password reset link has been sent.',
      backToLogin: 'Back to sign-in',
    },
    reset: {
      title: 'Choose a new password',
      subtitle: 'Set the new password for your account',
      totpLabel: 'Authenticator code',
      totpNote: 'This account has two-factor authentication — enter the 6-digit code from your app.',
      submit: 'Reset password',
      submitting: 'Resetting…',
      success: 'Your password has been reset. You can now sign in.',
      goLogin: 'Go to sign-in',
      invalid: 'This reset link is invalid or has expired.',
    },
  },
  dashboard: {
    greeting: 'Hello {{name}}',
    title: 'Your documents',
    subtitle: 'Everything shared with you, in one place.',
    searchPlaceholder: 'Filter by title or collection…',
    ungrouped: 'Other documents',
    open: 'Open',
    updated: 'Updated {{date}}',
    emptyTitle: 'No documents yet',
    emptyBody: 'No documents have been shared with you yet — please contact your administrator.',
    errorLoad: 'Your documents could not be loaded.',
    retry: 'Try again',
    noMatches: 'No documents match your filter.',
  },
  viewer: {
    loading: 'Opening document…',
    unavailableTitle: 'Document not available',
    unavailableBody: 'This document is no longer available, or your access has changed.',
    backToDocuments: 'Back to documents',
    reload: 'Reload document',
  },
  publish: {
    title: 'Publishing',
    subtitle: 'Upload self-contained HTML documents and manage who can read them.',
    publisherLabel: 'Category',
    publisherScopeHint: 'The documents and collections below belong to this category. Recipients are granted from the whole user directory.',
    noPublisher: 'Your account does not administer any category. Contact the platform admin.',
    upload: {
      title: 'Publish a document',
      drop: 'Drag & drop an HTML file here',
      browse: 'or browse',
      titleLabel: 'Title (optional — defaults to the file name)',
      titlePlaceholder: 'e.g. Weekly report — Circet',
      expiryLabel: 'Expiry date (optional)',
      grantsLabel: 'Give access to',
      grantsHint: 'You can also manage access after publishing.',
      submit: 'Publish',
      uploading: 'Publishing…',
      success: '"{{title}}" has been published.',
      validatorOk: 'Self-contained check passed.',
      validatorWarn: 'Advisory: external references detected ({{count}}). The sandbox blocks them at view time.',
      onlyHtml: 'Only .html files are accepted.',
      noGrantsWarn: 'Published — but not shared with anyone yet. Use “Manage access” to grant viewers.',
      collectionLabel: 'Collections',
      collectionHint: 'optional — pick one or more',
      noCollection: '— none (ungrouped) —',
      noCollectionsYet: 'No collections yet — the document goes to “Uncategorized”.',
      dupSameDept: 'This document is already published in this category.',
    },
    docs: {
      title: 'Published documents',
      empty: 'Nothing published yet. Your first document will appear here.',
      notShared: 'Not shared',
      notSharedHint: 'Published but granted to no one — no viewer can see it yet.',
      cols: {
        title: 'Title',
        status: 'Status',
        selfContained: 'Self-contained',
        version: 'v',
        updated: 'Updated',
        access: 'Access',
        actions: '',
      },
      manageAccess: 'Manage access',
      edit: 'Edit',
      users: '{{count}} users',
      ownerDept: 'Owning category',
      linkedDept: 'Linked category',
    },
    edit: {
      title: 'Edit document',
      titleLabel: 'Title',
      displayNameLabel: 'Display name for your category (optional)',
      secondaryHint: 'This document is owned by another category. You can manage its collections and access for your category and give it your own display name — but not change its title or delete it.',
      collectionLabel: 'Collections',
      expiryLabel: 'Expiry date (optional)',
      save: 'Save changes',
      saved: 'Document updated.',
      unlink: 'Remove from my category',
      unlinkConfirm: 'Remove this document from your category? It stays available to its owning category.',
      unlinked: 'Removed from your category.',
    },
    link: {
      title: 'This document already exists',
      subtitle: '“{{title}}” was already published by {{owner}}. Link it into your collections instead of uploading a copy.',
      nameLabel: 'Display name for your category (optional)',
      namePlaceholder: 'Keep the original title',
      collectionsLabel: 'Add to your collections',
      submit: 'Link to my category',
      success: 'Document linked to your category.',
    },
    status: { PUBLISHED: 'Published', DRAFT: 'Draft', EXPIRED: 'Expired', DELETED: 'Deleted' },
    access: {
      title: 'Who can read "{{title}}"?',
      subtitle: 'Anyone on the platform can be given access to this document.',
      searchUsers: 'Filter users…',
      noUsers: 'No users on the platform yet — create them under Users.',
      save: 'Save access',
      saved: 'Access updated: {{added}} added, {{removed}} removed.',
    },
  },
  admin: {
    title: 'Users',
    subtitle: 'External accounts that can sign in to UniPortal.',
    create: 'New user',
    searchPlaceholder: 'Search name, email or company…',
    cols: {
      name: 'Name',
      company: 'Company',
      auth: 'Sign-in',
      publishers: 'Categories',
      status: 'Status',
      twofa: '2FA',
      lastLogin: 'Last sign-in',
    },
    active: 'Active',
    inactive: 'Inactive',
    never: 'Never',
    platformAdmin: 'Platform admin',
    allPublishers: 'All categories',
    dialog: {
      createTitle: 'Create a user',
      editTitle: 'Edit user',
      saveChanges: 'Save changes',
      email: 'Email address',
      displayName: 'Display name',
      company: 'Company (optional)',
      language: 'Language',
      authType: 'Sign-in method',
      authTypes: {
        LOCAL: 'Password + authenticator (external user)',
        ENTRA_ID: 'Microsoft SSO only',
        BOTH: 'Microsoft SSO or password',
      },
      publisher: 'Category',
      noPublisherOption: '— none —',
      publisherRole: 'Role in category',
      publisherRoles: { VIEWER: 'Viewer', PUBLISHER_ADMIN: 'Publisher admin' },
      role: 'Platform role',
      roles: { VIEWER: 'Standard', PLATFORM_ADMIN: 'Platform admin' },
      userType: 'User type',
      userTypes: { REGULAR: 'Regular user', PUBLISHER: 'Publisher', ADMIN: 'Admin' },
      department: 'Category',
      departmentPlaceholder: '— choose —',
      categories: 'Categories',
      categoriesHint: 'one or more',
      noCategories: 'No categories yet.',
      submit: 'Create user',
      creating: 'Creating…',
      created: '{{email}} created — welcome email sent.',
      convertTitle: 'Change sign-in method',
      convertSubmit: 'Convert',
    },
    actions: {
      menu: 'Actions',
      edit: 'Edit…',
      manageGrants: 'Manage grants',
      resendWelcome: 'Resend welcome email',
      resetPassword: 'Send password reset',
      reset2fa: 'Reset 2FA',
      convertAuth: 'Change sign-in method',
      deactivate: 'Deactivate',
      activate: 'Activate',
      delete: 'Delete…',
      confirmDelete: 'Delete {{name}}? Their access and devices are removed permanently.',
    },
    toast: {
      welcomeSent: 'Welcome email sent.',
      resetSent: 'Password reset email sent.',
      twofaReset: '2FA has been reset.',
      converted: 'Sign-in method updated.',
      updated: 'User updated.',
      deleted: 'User deleted.',
    },
    grants: {
      title: 'Manage grants — {{name}}',
      subtitle: 'Choose the collections and individual documents this person can read.',
      collections: 'Collections',
      documents: 'Individual documents',
      noCollections: 'No collections available.',
      nonCollection: 'Uncategorized',
      nonCollectionHint: '(documents not in any collection)',
      searchDocs: 'Search a document to add…',
      noDocs: 'No documents available.',
      added: 'Added',
      saved: 'Grants updated.',
      save: 'Save grants',
    },
  },
  publishers: {
    title: 'Categories',
    subtitle: 'Internal categories that publish documents through UniPortal.',
    create: 'New category',
    cols: {
      name: 'Name',
      slug: 'Slug',
      status: 'Status',
      members: 'Publishers',
      documents: 'Documents',
      collections: 'Collections',
    },
    active: 'Active',
    disabled: 'Disabled',
    empty: 'No categories yet. Create the first one to start publishing.',
    dialog: {
      createTitle: 'Create a category',
      renameTitle: 'Rename category',
      name: 'Name',
      namePlaceholder: 'e.g. HC Contractor Reports',
      slug: 'Slug (optional)',
      slugHint: 'Lowercase letters, numbers and hyphens. Used in storage and sync paths — cannot be changed later. Auto-generated from the name if left blank.',
      submit: 'Create category',
      creating: 'Creating…',
      save: 'Save',
      saving: 'Saving…',
    },
    actions: {
      menu: 'Actions',
      rename: 'Rename',
      disable: 'Disable',
      enable: 'Enable',
      manageCollections: 'Manage collections',
      manageAdmins: 'Manage publishers',
      delete: 'Delete…',
      confirmDelete: 'Delete category "{{name}}"? This also removes its collections and memberships.',
    },
    admins: {
      title: 'Publishers — {{name}}',
      subtitle: 'People allowed to publish and manage this category’s content.',
      search: 'Search a user to add as a publisher…',
      current: 'Current publishers',
      none: 'No publishers yet.',
      noResults: 'No matching users.',
      remove: 'Remove',
      close: 'Close',
    },
    collections: {
      title: 'Collections — {{name}}',
      subtitle: 'Groups of documents within this category (e.g. one per contractor).',
      add: 'Add collection',
      namePlaceholder: 'Collection name',
      empty: 'No collections yet.',
      documentsCount: '{{count}} documents',
      delete: 'Delete',
      confirmDelete: 'Delete collection "{{name}}"?',
      close: 'Close',
    },
    toast: {
      created: 'Category "{{name}}" created.',
      updated: 'Category updated.',
      deleted: 'Category deleted.',
      disabled: 'Category disabled.',
      enabled: 'Category enabled.',
      collectionCreated: 'Collection added.',
      collectionDeleted: 'Collection deleted.',
      adminAdded: 'Publisher added.',
      adminRemoved: 'Publisher removed.',
      hasDocuments: 'This category still has documents — disable it instead.',
    },
  },
  collections: {
    title: 'Collections',
    subtitle:
      'Group a category’s documents (e.g. one collection per contractor). Grant users access to a collection and every document in it — including future SFTP/sync drops — becomes available to them.',
    categoryLabel: 'Category',
    new: 'New collection',
    noCategory: 'You don’t administer any category yet. Ask a platform admin to add you as a publisher.',
    empty: 'No collections yet. Create one to start grouping documents.',
    documentsCount: '{{count}} documents',
    systemBadge: 'System',
    uncategorizedHint: 'System default — documents with no collection land here. It can’t be renamed or deleted.',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Delete collection “{{name}}”? This can’t be undone.',
    hasDocuments: 'This collection still has documents — move or remove them first.',
    dialog: {
      createTitle: 'Create a collection',
      editTitle: 'Edit collection',
      name: 'Name',
      namePlaceholder: 'e.g. Circet',
      slug: 'Slug (optional)',
      slugHint:
        'Lowercase letters, numbers and hyphens. Used in sync/drop paths — can’t be changed later. Auto-generated from the name if left blank.',
      description: 'Description (optional)',
      descriptionPlaceholder: 'What this collection groups…',
      create: 'Create',
      creating: 'Creating…',
      save: 'Save',
      saving: 'Saving…',
    },
    toast: {
      created: 'Collection “{{name}}” created.',
      updated: 'Collection updated.',
      deleted: 'Collection deleted.',
    },
  },
  channels: {
    title: 'Drop folders',
    subtitle:
      'Bind a watched SFTP/sync folder to a collection. Any HTML dropped there is auto-published into that collection and becomes readable by whoever is already granted it — no per-file admin.',
    categoryLabel: 'Category',
    new: 'New drop folder',
    noCategory: 'You don’t administer any category yet. Ask a platform admin to add you as a publisher.',
    empty: 'No drop folders yet. Create one to let a pipeline publish by dropping files.',
    intoCollection: 'Publishes into',
    copyPath: 'Copy path',
    delete: 'Delete',
    confirmDelete: 'Delete drop folder “{{name}}”? Already-published documents keep their access.',
    dialog: {
      title: 'Create a drop folder',
      subtitle: 'Pick the collection that files dropped here should be published into.',
      collection: 'Collection',
      collectionPlaceholder: 'Select a collection…',
      noCollections: 'Create a collection first, then bind a drop folder to it.',
      label: 'Label (optional)',
      labelPlaceholder: 'e.g. Circet weekly reports',
      create: 'Create',
      creating: 'Creating…',
    },
    toast: {
      created: 'Drop folder created.',
      deleted: 'Drop folder deleted.',
    },
  },
  guide: {
    basic: {
      eyebrow: 'Guide',
      title: 'Welcome to UniPortal',
      intro: 'UniPortal is your secure reading room — the documents shared with you, all in one place.',
      cards: {
        documents: { title: 'Your documents', body: 'You see only the documents shared with you, grouped by category and collection on your Documents page.' },
        reading: { title: 'Reading a document', body: 'Click any document to open it in a secure, read-only viewer in a new tab. Nothing is downloaded to your device.' },
        access: { title: 'Need a document?', body: 'If something you expect isn’t there, ask the team that shares documents with you to grant you access.' },
        security: { title: 'Signing in', body: 'Your account is protected with a password and a 6-digit authenticator code, or with your Microsoft account.' },
      },
    },
    hero: {
      eyebrow: 'Guide',
      title: 'How UniPortal works',
      intro: 'UniPortal lets internal teams publish self-contained documents and share them securely with external users — no passwords or links to forward, just access granted to the right people.',
      flow: {
        publish: { title: 'Publish', body: 'A team uploads a document' },
        organise: { title: 'Organise', body: 'It is filed into collections' },
        grant: { title: 'Grant', body: 'Access is given to users' },
        read: { title: 'Read', body: 'They read it, read-only' },
      },
    },
    hierarchy: {
      title: 'The hierarchy',
      subtitle: 'Categories contain collections, which contain documents.',
      publisherTag: 'Category · internal area',
      publisherExample: 'HC Contractor Reports',
      collections: { circet: 'Circet', ctl: 'CTL', nonCollection: 'Uncategorized' },
      doc: {
        circet: { 0: 'Weekly report — wk 23', 1: 'Site audit — Antwerp' },
        ctl: { 0: 'Progress report — May', 1: 'Quality checklist' },
        nonCollection: { 0: 'Kick-off notes', 1: 'Misc. handover' },
      },
      nonCollectionNote: 'A system folder, one per category, that automatically holds any document not filed into a collection.',
      legend: {
        publisher: { term: 'Category', def: 'an internal Unifiber area that publishes documents.' },
        collection: { term: 'Collection', def: 'a named group of documents, e.g. one per contractor.' },
        document: { term: 'Document', def: 'one self-contained HTML file shared with users.' },
      },
    },
    m2m: {
      title: 'Many-to-many links',
      subtitle: 'A single document can live in several places at once — the subtle part.',
      inBadge: 'is in',
      linkBadge: 'links to',
      docCollections: {
        title: 'One document, many collections',
        body: 'The same document can be filed into several collections at once. It shows up under each one, but there is still only a single document behind them.',
        doc: 'Document',
        col: { circet: 'Circet', shared: 'Shared reports' },
      },
      docPublishers: {
        title: 'One document, many categories',
        body: 'If a second category uploads the exact same file, UniPortal detects the duplicate and links the existing document instead of storing a copy.',
        deptA: 'Category A',
        deptB: 'Category B',
        primary: 'Owner',
        secondary: 'Linked',
        oneDoc: 'One document',
        note: 'The owning category is primary. A second category manages its own collections and access for the document, but cannot delete it.',
      },
    },
    access: {
      title: 'Who can see what',
      subtitle: 'An external user only ever sees the documents granted to them.',
      or: 'OR',
      pathA: { tag: 'Path A', title: 'Grant a whole collection', body: 'The user can read every document in that collection — including documents added later.' },
      pathB: { tag: 'Path B', title: 'Grant a single document', body: 'The user can read just that one document, regardless of which collections it belongs to.' },
      result: { title: 'A user sees a document if either path applies', body: 'They have a direct grant on the document, OR a grant on any collection the document is in.' },
      formula: { visible: 'Can see', docGrant: 'document grant', or: 'OR', colGrant: 'grant on any of its collections' },
    },
    roles: {
      title: 'Roles & rights',
      subtitle: 'Three kinds of account, with very different powers.',
      scopeLabel: 'Scope',
      viewer: {
        name: 'Regular viewer',
        tagline: 'External reader',
        summary: 'Signs in to read the documents shared with them — nothing more. A clean, read-only reading room.',
        can: ['Sign in securely (password + 2FA, or Microsoft)', 'See only documents granted to them', 'Read documents in a sandboxed viewer'],
        scope: 'Their own granted documents only',
      },
      publisher: {
        name: 'Publisher',
        tagline: 'Publishing staff',
        summary: 'Publishes and manages the documents and collections of one or more categories, and decides who can read them.',
        can: ['Upload and edit documents for their categories', 'Create and organise collections', 'Grant access to any user on the platform'],
        scope: 'Only the categories they belong to',
      },
      admin: {
        name: 'Platform admin',
        tagline: 'Full control',
        summary: 'Manages the whole platform — every category, every user, every document and collection.',
        can: ['Manage all categories', 'Create and manage all user accounts', 'Oversee every document, collection and grant'],
        scope: 'The entire platform',
      },
    },
  },
} as const;
